import { supabase } from '../lib/supabase';
import { Event, Ticket, TicketOrder, EventAttendee } from '../types/event';

// Events
export const getEvents = async (filters: any = {}) => {
  let query = supabase
    .from('events')
    .select('*, organizer:users(id, name, email, avatar_url), contest:contests(id, name, logo_url)');

  if (filters.is_active !== undefined) {
    query = query.eq('is_active', filters.is_active);
  }

  if (filters.organizer_id) {
    query = query.eq('organizer_id', filters.organizer_id);
  }

  if (filters.contest_id) {
    query = query.eq('contest_id', filters.contest_id);
  }

  // Filter by upcoming events
  if (filters.upcoming) {
    query = query.gte('start_date', new Date().toISOString());
  }

  // Filter by past events
  if (filters.past) {
    query = query.lt('start_date', new Date().toISOString());
  }

  // Order by start_date
  query = query.order('start_date', { ascending: filters.ascending ?? true });

  const { data, error } = await query;
  if (error) throw error;
  return data as Event[];
};

export const getEventBySlug = async (slug: string) => {
  try {
    const { data: eventData, error: eventError } = await supabase
      .from('events')
      .select('*')
      .eq('slug', slug)
      .single();

    if (eventError) {
      console.error('Erreur lors de la récupération de l\'événement par slug:', eventError);
      throw eventError;
    }

    // Récupérer les données liées
    const { data: relatedData, error: relatedError } = await supabase
      .from('events')
      .select(`
        *,
        organizer:users(id, name, email, avatar_url),
        contest:contests(id, name, logo_url)
      `)
      .eq('id', eventData.id)
      .single();

    if (relatedError) {
      console.error('Erreur lors de la récupération des données liées:', relatedError);
      return eventData as Event;
    }

    return relatedData as Event;
  } catch (error) {
    console.error('Erreur dans getEventBySlug:', error);
    throw error;
  }
};

export const getEventById = async (id: string) => {
  try {
    // D'abord, récupérer l'événement sans les jointures pour s'assurer qu'il existe
    const { data: eventData, error: eventError } = await supabase
      .from('events')
      .select('*')
      .eq('id', id)
      .single();

    if (eventError) {
      console.error('Erreur lors de la récupération de l\'événement:', eventError);
      throw eventError;
    }

    if (!eventData) {
      throw new Error(`Événement avec l'ID ${id} non trouvé`);
    }

    // Ensuite, récupérer les relations si nécessaire
    if (eventData.organizer_id) {
      const { data: organizerData } = await supabase
        .from('users')
        .select('id, name, email, avatar_url')
        .eq('id', eventData.organizer_id)
        .single();
      
      if (organizerData) {
        eventData.organizer = organizerData;
      }
    }

    if (eventData.contest_id) {
      const { data: contestData } = await supabase
        .from('contests')
        .select('id, name, logo_url')
        .eq('id', eventData.contest_id)
        .single();
      
      if (contestData) {
        eventData.contest = contestData;
      }
    }

    return eventData as Event;
  } catch (error) {
    console.error('Erreur dans getEventById:', error);
    throw error;
  }
};

export const createEvent = async (event: Partial<Event>) => {
  const { data, error } = await supabase
    .from('events')
    .insert(event)
    .select()
    .single();

  if (error) throw error;
  return data as Event;
};

export const updateEvent = async (id: string, event: Partial<Event>) => {
  const { data, error } = await supabase
    .from('events')
    .update(event)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Event;
};

export const deleteEvent = async (id: string) => {
  const { error } = await supabase
    .from('events')
    .delete()
    .eq('id', id);

  if (error) throw error;
  return true;
};

// Tickets
export const getTickets = async (eventId: string) => {
  const { data, error } = await supabase
    .from('tickets')
    .select('*')
    .eq('event_id', eventId)
    .eq('is_active', true)
    .order('price');

  if (error) throw error;
  return data as Ticket[];
};

export const getTicketById = async (id: string) => {
  const { data, error } = await supabase
    .from('tickets')
    .select('*, event:events(id, title, start_date, end_date, location)')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as Ticket;
};

export const createTicket = async (ticket: Partial<Ticket>) => {
  const { data, error } = await supabase
    .from('tickets')
    .insert(ticket)
    .select()
    .single();

  if (error) throw error;
  return data as Ticket;
};

export const updateTicket = async (id: string, ticket: Partial<Ticket>) => {
  const { data, error } = await supabase
    .from('tickets')
    .update(ticket)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Ticket;
};

export const deleteTicket = async (id: string) => {
  const { error } = await supabase
    .from('tickets')
    .delete()
    .eq('id', id);

  if (error) throw error;
  return true;
};

// Ticket Orders
export const createTicketOrder = async (order: Partial<TicketOrder>) => {
  console.log('createTicketOrder - Données reçues:', { 
    ticket_id: order.ticket_id, 
    quantity: order.quantity,
    orderData: order
  });
  
  // Vérifier la disponibilité du ticket avant de créer la commande
  const { data: ticket, error: ticketError } = await supabase
    .from('tickets')
    .select('id, available_quantity, quantity')
    .eq('id', order.ticket_id)
    .single();

  if (ticketError) throw ticketError;
  
  // Vérifier si suffisamment de tickets sont disponibles
  if (ticket.available_quantity !== null && 
      ticket.available_quantity < (order.quantity || 1)) {
    throw new Error(`Seulement ${ticket.available_quantity} tickets disponibles`);
  }

  // Utiliser une transaction pour garantir l'intégrité des données
  // Validation/cast UUID pour user_id
  if (order && order.user_id && typeof order.user_id === 'string') {
    const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    if (!uuidRegex.test(order.user_id)) {
      throw new Error('user_id fourni n\'est pas un UUID valide');
    }
  }
  // Cast explicite du user_id en UUID côté SQL (via ::uuid)
  const order_data = {
    user_id: order.user_id ? `${order.user_id}` : null,
    amount: order.amount,
    currency: order.currency,
    status: order.status,
    payment_method: order.payment_method,
    email: order.email,
    phone: order.phone,
    attendee_name: order.attendee_name, // Ajout du nom du participant
    order_code: order.order_code
  };
  console.log('Appel RPC create_ticket_order avec:', {
    order_data,
    quantity_ordered: order.quantity || 1,
    ticket_id: order.ticket_id
  });
  
  const { data, error } = await supabase.rpc('create_ticket_order', {
    order_data,
    quantity_ordered: order.quantity || 1,
    ticket_id: order.ticket_id
  });

  if (error) {
    console.error('Erreur Supabase create_ticket_order:', error);
    throw error;
  }
  return data as TicketOrder;
};

export const getTicketOrderById = async (id: string) => {
  const { data, error } = await supabase
    .from('ticket_orders')
    .select('*, ticket:tickets(id, name, price, event_id, event:events(id, title, start_date, location)), user:users(id, name, email)')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as TicketOrder;
};

export const getTicketOrdersByUser = async (userId: string) => {
  const { data, error } = await supabase
    .from('ticket_orders')
    .select('*, ticket:tickets(id, name, price, event_id, event:events(id, title, start_date, location))')
    .eq('user_id', userId);

  if (error) throw error;
  return data as TicketOrder[];
};

export const getTicketOrdersByEmail = async (email: string) => {
  try {
    // Récupérer les commandes de tickets associées à cet email
    const { data: orders, error: ordersError } = await supabase
      .from('ticket_orders')
      .select('id, user_id, ticket_id, quantity, amount, currency, status, payment_id, payment_method, email, phone, attendee_name, order_code, created_at, updated_at')
      .eq('email', email)
      .order('created_at', { ascending: false });

    if (ordersError) throw ordersError;
    
    // Si aucune commande n'est trouvée, retourner un tableau vide
    if (!orders || orders.length === 0) {
      return [];
    }
    
    // Récupérer les informations des tickets séparément
    const ticketIds = orders.map(order => order.ticket_id);
    const { data: tickets, error: ticketsError } = await supabase
      .from('tickets')
      .select('id, name, price, ticket_type, event_id')
      .in('id', ticketIds);
      
    if (ticketsError) throw ticketsError;
    
    // Récupérer les informations des événements séparément
    const eventIds = tickets?.map(ticket => ticket.event_id).filter(id => id) || [];
    const { data: events, error: eventsError } = await supabase
      .from('events')
      .select('id, title, start_date, end_date, location, image_url')
      .in('id', eventIds);
      
    if (eventsError) throw eventsError;
    
    // Associer les tickets et les événements aux commandes
    const ordersWithDetails = orders.map(order => {
      const ticket = tickets?.find(t => t.id === order.ticket_id);
      const event = ticket ? events?.find(e => e.id === ticket.event_id) : null;
      
      return {
        ...order,
        ticket: ticket ? {
          ...ticket,
          event: event || null
        } : null
      };
    });

    // Récupérer les participants (attendees) associés à ces commandes
    if (ordersWithDetails && ordersWithDetails.length > 0) {
      const orderIds = ordersWithDetails.map(order => order.id);
      
      const { data: attendees, error: attendeesError } = await supabase
        .from('event_attendees')
        .select('id, qr_code, status, check_in_time, ticket_order_id')
        .in('ticket_order_id', orderIds);

      if (attendeesError) throw attendeesError;

      // Associer les participants à leurs commandes respectives
      const ordersWithAttendees = ordersWithDetails.map(order => {
        const orderAttendees = attendees?.filter(attendee => attendee.ticket_order_id === order.id) || [];
        return {
          ...order,
          attendees: orderAttendees
        };
      });

      return ordersWithAttendees as (TicketOrder & { attendees: EventAttendee[] })[];
    }

    return ordersWithDetails as TicketOrder[];
  } catch (error) {
    console.error('Erreur dans getTicketOrdersByEmail:', error);
    throw error;
  }
};

export const updateTicketOrderStatus = async (id: string, status: string, paymentId?: string) => {
  const updates: any = { status };
  // N'ajoute payment_id que si c'est un UUID valide
  const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  if (paymentId && uuidRegex.test(paymentId)) {
    updates.payment_id = paymentId;
  }

  const { data, error } = await supabase
    .from('ticket_orders')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as TicketOrder;
};

// Event Attendees
export const createEventAttendee = async (attendee: Partial<EventAttendee>) => {
  const { data, error } = await supabase
    .from('event_attendees')
    .insert(attendee)
    .select()
    .single();

  if (error) throw error;
  return data as EventAttendee;
};

export const getEventAttendees = async (eventId: string) => {
  try {
    // Utiliser une requête plus simple sans jointures complexes
    const { data, error } = await supabase
      .from('event_attendees')
      .select('id, event_id, created_at, updated_at, status, qr_code, check_in_time, ticket_order_id, user_id, attendee_name, attendee_email, attendee_phone, user:event_attendees_user_id_fkey(id, name, email)')
      .eq('event_id', eventId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erreur lors de la récupération des participants:', error);
      throw error;
    }

    // Récupérer les informations des utilisateurs séparément
    const attendeesWithDetails = await Promise.all(
      data.map(async (attendee) => {
        // Ajouter les détails de l'utilisateur si disponible
        if (attendee.user_id) {
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('id, name, email, phone')
            .eq('id', attendee.user_id)
            .single();

          if (!userError && userData) {
            attendee.user = userData;
          }
        }

        // Ajouter les détails de la commande de ticket si disponible
        if (attendee.ticket_order_id) {
          const { data: orderData, error: orderError } = await supabase
            .from('ticket_orders')
            .select('id, quantity, ticket_id')
            .eq('id', attendee.ticket_order_id)
            .single();

          if (!orderError && orderData) {
            attendee.ticket_order = orderData;

            // Ajouter les détails du ticket si disponible
            if (orderData.ticket_id) {
              const { data: ticketData, error: ticketError } = await supabase
                .from('tickets')
                .select('id, name, ticket_type, price, currency')
                .eq('id', orderData.ticket_id)
                .single();

              if (!ticketError && ticketData) {
                attendee.ticket_order.ticket = ticketData;
              }
            }
          }
        }

        return attendee;
      })
    );

    return attendeesWithDetails as EventAttendee[];
  } catch (error) {
    console.error('Erreur dans getEventAttendees:', error);
    throw error;
  }
};

export const getCommissionSettings = async (eventId: string) => {
  try {
    const { data, error } = await supabase
      .from('commission_settings')
      .select('*')
      .eq('event_id', eventId)
      .single();

    if (error) {
      // Si aucun paramètre n'est trouvé, utiliser les valeurs par défaut
      if (error.code === 'PGRST116') {
        return {
          platform_fee: 0.05,
          payment_fee: 0.02
        };
      }
      console.error('Erreur lors de la récupération des paramètres de commission:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Erreur dans getCommissionSettings:', error);
    // Retourner les valeurs par défaut en cas d'erreur
    return {
      platform_fee: 0.05,
      payment_fee: 0.02
    };
  }
};

export const updateCommissionSettings = async (eventId: string, platformFee: number, paymentFee: number) => {
  try {
    // Vérifier d'abord si un enregistrement existe déjà
    const { data: existingData, error: checkError } = await supabase
      .from('commission_settings')
      .select('id')
      .eq('event_id', eventId)
      .single();

    if (checkError && checkError.code !== 'PGRST116') {
      console.error('Erreur lors de la vérification des paramètres de commission:', checkError);
      throw checkError;
    }

    if (existingData) {
      // Mettre à jour l'enregistrement existant
      const { error: updateError } = await supabase
        .from('commission_settings')
        .update({
          platform_fee: platformFee,
          payment_fee: paymentFee,
          updated_at: new Date()
        })
        .eq('event_id', eventId);

      if (updateError) {
        console.error('Erreur lors de la mise à jour des paramètres de commission:', updateError);
        throw updateError;
      }
    } else {
      // Créer un nouvel enregistrement
      const { error: insertError } = await supabase
        .from('commission_settings')
        .insert({
          event_id: eventId,
          platform_fee: platformFee,
          payment_fee: paymentFee
        });

      if (insertError) {
        console.error('Erreur lors de la création des paramètres de commission:', insertError);
        throw insertError;
      }
    }

    return { success: true };
  } catch (error) {
    console.error('Erreur dans updateCommissionSettings:', error);
    throw error;
  }
};

export const getAttendeesByEmail = async (email: string) => {
  try {
    // Récupérer les participants par email sans utiliser de relations complexes
    const { data: attendees, error } = await supabase
      .from('event_attendees')
      .select('id, event_id, created_at, updated_at, status, qr_code, check_in_time, ticket_order_id, user_id, attendee_name, attendee_email, attendee_phone')
      .eq('attendee_email', email)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erreur lors de la récupération des tickets par email:', error);
      throw error;
    }

    // Si aucun participant n'est trouvé, retourner un tableau vide
    if (!attendees || attendees.length === 0) {
      return [];
    }

    // Récupérer les informations des événements séparément
    const eventIds = attendees.map(attendee => attendee.event_id).filter(id => id);
    const { data: events, error: eventsError } = await supabase
      .from('events')
      .select('id, title, start_date, end_date, location, image_url')
      .in('id', eventIds);

    if (eventsError) {
      console.error('Erreur lors de la récupération des événements:', eventsError);
      throw eventsError;
    }

    // Récupérer les informations des commandes séparément
    const orderIds = attendees.map(attendee => attendee.ticket_order_id).filter(id => id);
    const { data: orders, error: ordersError } = await supabase
      .from('ticket_orders')
      .select('id, ticket_id, quantity, amount, currency, status, order_code')
      .in('id', orderIds);

    if (ordersError) {
      console.error('Erreur lors de la récupération des commandes:', ordersError);
      throw ordersError;
    }

    // Récupérer les informations des tickets séparément
    const ticketIds = orders?.map(order => order.ticket_id).filter(id => id) || [];
    const { data: tickets, error: ticketsError } = await supabase
      .from('tickets')
      .select('id, name, ticket_type, price, currency')
      .in('id', ticketIds);

    if (ticketsError) {
      console.error('Erreur lors de la récupération des tickets:', ticketsError);
      throw ticketsError;
    }

    // Associer toutes les informations aux participants
    const attendeesWithDetails = attendees.map(attendee => {
      const event = events?.find(e => e.id === attendee.event_id);
      const order = orders?.find(o => o.id === attendee.ticket_order_id);
      const ticket = order ? tickets?.find(t => t.id === order.ticket_id) : null;

      return {
        ...attendee,
        event: event || null,
        ticket_order: order ? {
          ...order,
          ticket: ticket || null
        } : null
      };
    });

    return attendeesWithDetails as EventAttendee[];
  } catch (error) {
    console.error('Erreur dans getAttendeesByEmail:', error);
    throw error;
  }
};

export const getAttendeeByQrCode = async (qrCode: string) => {
  const { data, error } = await supabase
    .from('event_attendees')
    .select(`
      *,
      event:event_attendees_event_id_fkey(id, title),
      user:event_attendees_user_id_fkey(id, name, email),
      ticket_order:event_attendees_ticket_order_id_fkey(
        id,
        ticket_id,
        amount,
        ticket:ticket_orders_ticket_id_fkey(id, name, ticket_type, price, currency)
      )
    `)
    .eq('qr_code', qrCode)
    .single();

  if (error) throw error;
  return data as EventAttendee;
};

export const checkInAttendee = async (id: string) => {
  // Vérifier d'abord si l'attendee existe et n'est pas déjà checké
  const { data: existingAttendee, error: fetchError } = await supabase
    .from('event_attendees')
    .select('id, status')
    .eq('id', id)
    .single();

  if (fetchError) throw fetchError;
  
  // Si déjà checké, retourner une erreur claire
  if (existingAttendee.status === 'checked_in') {
    throw new Error('Ce participant a déjà été enregistré');
  }

  // Utiliser une RPC pour garantir l'atomicité de l'opération
  const { data, error } = await supabase.rpc('check_in_attendee', {
    attendee_id: id,
    new_check_in_time: new Date().toISOString()
  });

  if (error) throw error;
  return data as EventAttendee;
};
